import { CircularProgress, makeStyles, useTheme } from "@seahorse/ui";
import { useState } from "react";
import { handleAddAccountId } from "../Utils/Utils";

interface SubscriptionCheckProps {
  accountId?: string;
}

const SubscriptionCheck: React.FC<SubscriptionCheckProps> = ({ accountId }) => {
  const theme = useTheme();
  const [accountIdInput, setAccountIdInput] = useState("");
  const [status, setStatus] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = () => {
    window.location.href = "/logout";
  };

  const handleSubmit = () => {
    handleAddAccountId(accountIdInput, setStatus, setError, setIsLoading);
  };

  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    navbar: {
      backgroundColor: theme.globallink.colors.DarkGrey[500],
      borderBottom: `1px solid `,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0.5rem 1rem",
    },
    navBrand: {
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
      color: "#E0E0E0",
    },
    logo: {
      verticalAlign: "middle",
      marginRight: "0.5rem",
      height: "30px",
      width: "auto",
    },
    logoutButton: {
      borderRadius: "5px",
      backgroundColor: "transparent",
      color: "#E0E0E0",
      border: `1px solid #E0E0E0`,
      padding: "0.3rem 0.6rem",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#E0E0E0",
        color: theme.globallink.colors.DarkGrey[500],
      },
    },
    contentWrapper: {
      flexGrow: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "1rem",
    },
    formContainer: {
      maxWidth: "400px",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
      borderRadius: "10px",
      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      padding: "2rem",
      textAlign: "center",
      width: "100%",
    },
    title: {
      marginBottom: "1rem",
      fontSize: "1.5rem",
      fontWeight: 600,
      color: "#E0E0E0",
    },
    alertSuccess: {
      backgroundColor: "#1B5E20",
      color: "#E0E0E0",
      borderRadius: "8px",
      boxShadow: "0px 2px 6px rgba(0, 128, 0, 0.2)",
      padding: "1rem",
      marginBottom: "1rem",
    },
    alertError: {
      backgroundColor: "#B71C1C",
      color: theme.globallink.colors.Slate[700],
      borderRadius: "8px",
      boxShadow: "0px 2px 6px rgba(128, 0, 0, 0.2)",
      padding: "1rem",
      marginBottom: "1rem",
    },
    alertWarning: {
      backgroundColor: theme.globallink.colors.Grey[400],
      color: theme.globallink.colors.Slate[700],
      borderRadius: "5px",
      padding: "1rem",
      marginBottom: "1rem",
    },
    alertInfo: {
      backgroundColor: theme.globallink.colors.Grey[400],
      color: theme.globallink.colors.Slate[700],
      borderRadius: "5px",
      padding: "1rem",
      marginBottom: "1rem",
    },
    inputField: {
      borderRadius: "5px",
      padding: "0.5rem",
      marginBottom: "1rem",
      width: "100%",
      boxSizing: "border-box",
      border: `1px solid #E0E0E0`,
      color: "#E0E0E0",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    submitButton: {
      borderRadius: "5px",
      backgroundColor: "#0288D1",
      color: "#E0E0E0",
      padding: "0.5rem 1rem",
      border: "none",
      width: "100%",
      cursor: "pointer",
      marginBottom: "1rem",
      "&:hover": {
        backgroundColor: "#01579B",
      },
    },
    smallText: {
      fontSize: "0.9rem",
      color: "#E0E0E0",
      marginTop: "1rem",
    },
  });

  const classes = useStyles();

  return (
    <div className={classes.container}>
      {/* Top Nav Bar */}

      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            minHeight: "100vh",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.contentWrapper}>
          <div className={classes.formContainer}>
            <h2 className={classes.title}>Subscription</h2>
            {status ? (
              <div className={classes.alertSuccess}>
                <h5 className="mb-3">Request Submitted!</h5>
                <p>
                  Your request has been sent to the account manager. Please wait
                  for their approval.
                </p>
              </div>
            ) : error ? (
              <div className={classes.alertError}>
                <h5 className="mb-3">Error!</h5>
                <p>Could not find the Account!</p>
              </div>
            ) : (
              <>
                <div className={classes.alertWarning}>
                  We couldn't find an active subscription associated with your
                  account.
                </div>
                {!accountId && (
                  <>
                    <div className={classes.alertInfo}>
                      Have the Account ID?
                    </div>
                    <input
                      type="text"
                      className={classes.inputField}
                      placeholder="Enter your Account ID"
                      value={accountIdInput}
                      onChange={(e) => setAccountIdInput(e.target.value)}
                    />
                    <button
                      className={classes.submitButton}
                      onClick={handleSubmit}
                    >
                      Submit
                    </button>
                  </>
                )}
                <p className={classes.smallText}>
                  If you don't have an Account ID, please contact your system
                  administrator or EMAlpha Admin.
                </p>
                <p>
                  Email:{" "}
                  <a href="mailto:sales@emalpha.com">sales@emalpha.com</a>
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCheck;
